import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  Button,
  Card,
  CardContent,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  zodResolver,
} from "@devhop/ui";
import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useCreateOvertimeRequest, useOtRulesQuery } from "../api/queries";
import { OT_LIST_SEARCH_DEFAULTS } from "../constants";
import { z } from "zod";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { HrTour } from "../tour/HrTour";
import { useHrTour } from "../tour/useHrTour";
import { overtimeRequestFormTourSteps } from "../tour/hrTourSteps";

const OvertimeFormSchema = z
  .object({
    workDate: z.string().min(1, "ກະລຸນາເລືອກວັນທີ"),
    startTime: z.string().regex(/^\d{2}:\d{2}$/, "ຮູບແບບ HH:mm"),
    endTime: z.string().regex(/^\d{2}:\d{2}$/, "ຮູບແບບ HH:mm"),
    dayType: z.enum(["weekday", "holiday"]),
    reason: z.string().optional(),
  })
  .refine(
    (data) => {
      const [sh, sm] = data.startTime.split(":").map(Number);
      const [eh, em] = data.endTime.split(":").map(Number);
      const startMins = (sh ?? 0) * 60 + (sm ?? 0);
      const endMins = (eh ?? 0) * 60 + (em ?? 0);
      return endMins > startMins || (endMins < startMins && endMins > 0);
    },
    { message: "ເວລາສິ້ນສຸດຕ້ອງຫຼາຍກວ່າເວລາເລີ່ມຕົ້ນ", path: ["endTime"] },
  );

type OvertimeFormValues = z.infer<typeof OvertimeFormSchema>;

const DAY_TYPE_FALLBACK: { value: "weekday" | "holiday"; label: string }[] = [
  { value: "weekday", label: "ວັນທຳມະດາ" },
  { value: "holiday", label: "ວັນພັກ" },
];

export function OvertimeRequestNewPage() {
  const navigate = useNavigate({ from: "/app/hr/overtime/requests/new" });
  const createOt = useCreateOvertimeRequest();
  const tour = useHrTour("hr-ot-form-tour-completed");
  const otRules = useOtRulesQuery();

  const dayTypeOptions = useMemo(() => {
    const rules = otRules.data ?? [];
    if (rules.length === 0) return DAY_TYPE_FALLBACK;
    const seen = new Set<string>();
    return rules
      .filter((r) => {
        if (seen.has(r.dayType)) return false;
        seen.add(r.dayType);
        return true;
      })
      .map((r) => ({ value: r.dayType, label: r.name }));
  }, [otRules.data]);

  const methods = RHF.useForm<OvertimeFormValues>({
    resolver: zodResolver(OvertimeFormSchema),
    defaultValues: {
      workDate: "",
      startTime: "18:00",
      endTime: "20:00",
      dayType: "weekday",
      reason: "",
    },
  });

  const onSubmit = (values: OvertimeFormValues) => {
    createOt.mutate(
      {
        workDate: values.workDate,
        startTime: values.startTime,
        endTime: values.endTime,
        dayType: values.dayType,
        reason: values.reason ?? undefined,
      },
      {
        onSuccess: () => {
          navigate({ to: "/app/hr/overtime/requests", search: OT_LIST_SEARCH_DEFAULTS });
        },
      },
    );
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ສ້າງຄຳຂໍ OT</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ລາຍງານການເຮັດວຽກລ່ວງເວລາ — ວັນທີ, ເວລາເລີ່ມ–ສິ້ນສຸດ, ເຫດຜົນ
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/app/hr/overtime/requests", search: OT_LIST_SEARCH_DEFAULTS })}
            >
              <ArrowLeft className="mr-1 size-4" /> ກັບຄືນ
            </Button>
          </div>
        </div>

        <div className="mx-auto w-full max-w-2xl space-y-6">
          <Card>
            <CardContent>
              <FormRoot<OvertimeFormValues>
                methods={methods}
                onSubmit={onSubmit}
                className="w-full space-y-6"
              >
                <RHF.Controller
                  name="workDate"
                  render={({ field }) => (
                    <FormInput label="ວັນທີ" type="date" {...field} />
                  )}
                />
                <RHF.Controller
                  name="dayType"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label htmlFor="dayType" className="font-medium text-sm">ປະເພດວັນ</label>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger id="dayType">
                          <SelectValue placeholder="ເລືອກ" />
                        </SelectTrigger>
                        <SelectContent>
                          {dayTypeOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <RHF.Controller
                    name="startTime"
                    render={({ field }) => (
                      <FormInput
                        label="ເວລາເລີ່ມຕົ້ນ (HH:mm)"
                        type="time"
                        {...field}
                      />
                    )}
                  />
                  <RHF.Controller
                    name="endTime"
                    render={({ field }) => (
                      <FormInput
                        label="ເວລາສິ້ນສຸດ (HH:mm)"
                        type="time"
                        {...field}
                      />
                    )}
                  />
                </div>
                <RHF.Controller
                  name="reason"
                  render={({ field }) => (
                    <FormTextarea
                      label="ເຫດຜົນ"
                      placeholder="ອະທິບາຍວ່າເຮັດວຽກຫຍັງ (ຖ້າມີ)"
                      rows={3}
                      {...field}
                    />
                  )}
                />
                <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate({ to: "/app/hr/overtime/requests", search: OT_LIST_SEARCH_DEFAULTS })}
                  >
                    ຍົກເລີກ
                  </Button>
                  <Button
                    data-tourid="hr-ot-submit-button"
                    type="submit"
                    disabled={createOt.isPending}
                    className="bg-black text-white hover:bg-black/90"
                  >
                    {createOt.isPending ? "ກຳລັງສົ່ງ..." : "ສົ່ງຄຳຂໍ"}
                  </Button>
                </div>
              </FormRoot>
            </CardContent>
          </Card>
        </div>
        <HrTour
          steps={overtimeRequestFormTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
